import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AuthContext = createContext(null);

// Token storage
function saveTokens(access, refresh) {
  try {
    if (access) localStorage.setItem('lindey_access_token', access);
    if (refresh) localStorage.setItem('lindey_refresh_token', refresh);
  } catch (e) { /* */ }
}

function getAccessToken() {
  try { return localStorage.getItem('lindey_access_token'); } catch { return null; }
}

function getRefreshToken() {
  try { return localStorage.getItem('lindey_refresh_token'); } catch { return null; }
}

function clearTokens() {
  try { localStorage.removeItem('lindey_access_token'); localStorage.removeItem('lindey_refresh_token'); } catch { /* */ }
}

// Axios interceptor — attach Bearer token to every request
axios.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  // Do NOT use withCredentials — CORS blocks * origin + credentials
  config.withCredentials = false;
  return config;
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    const token = getAccessToken();
    if (!token) {
      setUser(false);
      setLoading(false);
      return;
    }
    try {
      const { data } = await axios.get(`${API}/auth/me`);
      setUser(data);
    } catch {
      // Try refresh
      const rt = getRefreshToken();
      if (rt) {
        try {
          const { data: rd } = await axios.post(`${API}/auth/refresh`, { refresh_token: rt });
          if (rd.access_token) {
            saveTokens(rd.access_token, null);
            const { data } = await axios.get(`${API}/auth/me`);
            setUser(data);
            setLoading(false);
            return;
          }
        } catch { /* */ }
      }
      clearTokens();
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const login = async (email, password, keepSignedIn = false) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password, keep_signed_in: keepSignedIn });
    saveTokens(data.access_token, data.refresh_token);
    setUser(data);
    return data;
  };

  const register = async (name, email, password) => {
    const { data } = await axios.post(`${API}/auth/register`, { name, email, password });
    saveTokens(data.access_token, data.refresh_token);
    try { localStorage.setItem('lindey_new_user', '1'); } catch { /* */ }
    setUser(data);
    return data;
  };

  const logout = async () => {
    try { await axios.post(`${API}/auth/logout`, {}); } catch { /* */ }
    clearTokens();
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
